import { forwardRef } from 'preact/compat'
import { BRAND_CLASSES } from '@/constants/theme'
import { getFieldLabelClasses, getInputSizing, type LabelSize, type ControlDensity } from './formLabelUtils'

export interface DateTimePickerProps extends Omit<JSX.IntrinsicElements['input'], 'type'> {
    label?: string
    required?: boolean
    error?: string
    helperText?: string
    /** Shown in place of the helper text, in amber: something to know that does not stop the save. */
    warning?: string
    containerClassName?: string
    className?: string
    /** Label size variant */
    labelSize?: LabelSize
    /** Field density: 'compact' tightens padding/radius/text for panel-style forms. */
    density?: ControlDensity
    /**
     * Escape hatch so the picker can be driven as a controlled input (value/onChange) — not just
     * via RHF `register()` spreads. Mirrors the index signature on the common Select; preact/compat's
     * forwardRef typing otherwise strips the intrinsic input attrs.
     */
    [key: string]: unknown
}

/**
 * Common DateTimePicker component for date and time selection
 * Integrates with React Hook Form via forwardRef
 * Uses HTML5 datetime-local input type for native browser support
 *
 * @example
 * ```tsx
 * // Basic usage with React Hook Form
 * <DateTimePicker
 *   label="Start Time"
 *   required
 *   error={errors.startTime?.message}
 *   {...register('startTime', { required: 'Start time is required' })}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With validation
 * <DateTimePicker
 *   label="End Time"
 *   required
 *   error={errors.endTime?.message}
 *   helperText="Must be after start time"
 *   {...register('endTime', {
 *     required: 'End time is required',
 *     validate: (value) => {
 *       const start = getValues('startTime')
 *       if (start && value && new Date(value) <= new Date(start)) {
 *         return 'End time must be after start time'
 *       }
 *       return true
 *     }
 *   })}
 * />
 * ```
 */
export const DateTimePicker = forwardRef<HTMLInputElement, DateTimePickerProps>(
    ({ label, required, error, helperText, warning, containerClassName, className, labelSize, density = 'comfortable', ...props }, ref) => {
        const inputClasses = `
            w-full border ${getInputSizing(density)}
            ${BRAND_CLASSES.INPUT.NORMAL}
            [color-scheme:light] dark:[color-scheme:dark]
            focus:outline-none focus:ring-2 focus:border-transparent
            ${error
                ? BRAND_CLASSES.INPUT.ERROR
                : BRAND_CLASSES.FOCUS.PRIMARY
            }
            ${className || ''}
        `.trim().replace(/\s+/g, ' ')

        return (
            <div className={containerClassName}>
                {label && (
                    <label className={getFieldLabelClasses(density, labelSize)}>
                        {label}
                        {required && <span className={BRAND_CLASSES.INPUT.REQUIRED}> *</span>}
                    </label>
                )}
                <input
                    ref={ref}
                    type="datetime-local"
                    className={inputClasses}
                    {...props}
                />
                {error && (
                    <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>
                        {error}
                    </p>
                )}
                {!error && warning && (
                    <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.HELPER_WARNING}`}>
                        {warning}
                    </p>
                )}
                {!error && !warning && helperText && (
                    <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.HELPER}`}>
                        {helperText}
                    </p>
                )}
            </div>
        )
    }
)

DateTimePicker.displayName = 'DateTimePicker'
